import { getEntitySubdomain, getEntityUuid } from '@opengov/app-core/entity/selectors';
import { modalActionCreators } from '@opengov/component-library-classic/module/modals';
import { selectModalComponentType } from '@opengov/component-library-classic/module/modals/selectors';
import { createSelector, createStructuredSelector } from '@opengov/compose-app/reselect';
import { IReport, ISavedView } from '@opengov/reporting-types';

import {
  multiDatasetReportEditPath,
  reportGalleryPath,
  reportSavedViewsPath,
  reportSettingsPath,
  selectReportExplorerPath,
  multiDatasetReportViewPath,
  embedTestPath,
  multiDatasetReportEditPathReactRouterVersion,
  multiDatasetReportViewPathReactRouterVersion,
  reportSettingsPathReactRouterVersion,
  reportSavedViewSuffix,
  makeReportExplorerPathReactRouterVersion,
  reportSavedViewsPathReactRouterVersion,
  reportGalleryPathReactRouterVersion,
  reportSavedViewEditPathReactRouterVersion,
  reportSavedViewEditPath
} from '../../../../module/routes/paths';
import { IPageHeaderProps } from '../../../common/PageHeader';
import { reportPdfEffects } from '../../../reportExplorer/ExportReportPdfModal/exportReportPdfEffects';
import {
  selectPrintableDetailTableProps,
  selectPrintableSummaryTableProps
} from '../../../reportExplorer/ExportReportPdfModal/exportReportPdfSelector';
import {
  selectCurrentSourceUpdatedAt,
  selectDataTableTotalRowCount,
  selectFetchingPdfExportData,
  selectDataTableStatus,
  selectVisualizationStatus,
  selectPdfExportTooMuchData,
  selectDimensionalTableQueryStatuses,
  selectDetailTableColumns
} from '../../../reportExplorer/selectors/selectReportExplorerState';
import { DataTableStatus, VisualizationStatus } from '../../../reportExplorer/types/reportExplorerTypes';
import { reportExplorerActionCreators } from '../../../reportExplorer/state/reportExplorerState';
import { selectReportExportVisualizationProps } from '../../../reportExplorer/ReportVisualization/selectors/selectReportVisualizationProps';
import { selectCurrentSavedViewEditSavedView } from '../../../reportSavedViewEdit/selectors/selectReportSavedViewEdit';
import { selectIsCoaMigrationEnabledFlag, selectPdfExportFlag } from '../../flags';
import { reportLocaleText } from '../../locale';
import { ReportModalTypes } from '../../modals/types';
import {
  selectCurrentFilters,
  selectCurrentReportConfiguration,
  selectDetailTableFilterBooleanOperators,
  selectDimensionalGlobalSegmentFilters,
  selectGroupByExpandedNodes,
  selectIsInStandardReport
} from '../../reportConfiguration/selectors';
import { IReportNavigationProps } from '../../ReportNavigation';
import { selectCurrentTemplate } from '../../reportTemplate/standardTemplateSelectors';
import { selectCurrentSavedView } from '../../savedViews/selectors';
import { reportEffects } from '../effects/reportEffects';

import { formatFiltersForPdfExport } from '../../filters/filterFormatters';
import { selectReportDataUpdateStatus } from '../../../accountSummary/MultiDataset/selectors/selectReportDatasetLenses';
import {
  selectCurrentReport,
  selectCurrentReportDescription,
  selectCurrentReportTitle,
  selectIsReportDimensional,
  selectIsReportsLoading,
  selectMuiToast,
  selectReports
} from './reportSelectors';
import { selectCanCreateReport } from './selectReportPermissions';
import { NavigateFunction } from 'react-router-dom';
import { hardNavigate, hybridNavigate } from '../../../../module/routes/routesUtils';
import { selectRouteName } from '../../routes/selectors/routeSelectors';

export const selectReportCountText = createSelector(selectIsReportsLoading, selectReports, (areLoading, reports) =>
  areLoading ? '' : reports.size.toString()
);

export const selectReportsHref = createSelector(getEntitySubdomain, (subdomain) =>
  subdomain ? reportGalleryPath(subdomain) : null
);

export const selectReportsHrefReactRouterVersion = createSelector(getEntitySubdomain, (subdomain) =>
  reportGalleryPathReactRouterVersion(subdomain)
);

export const selectEmbedTestHref = createSelector(getEntitySubdomain, (subdomain) =>
  subdomain ? embedTestPath(subdomain) : null
);

export const selectDashboardHomeHref = createSelector(getEntitySubdomain, (subdomain) => `/dashboards/${subdomain}`);

export const selectLegacyReportingHref = createSelector(getEntitySubdomain, (subdomain) =>
  subdomain ? `${window.location.origin}/reports#/${subdomain}` : null
);

export const selectNavigateToReportExplorer = createSelector(
  getEntitySubdomain,
  selectReportExplorerPath,
  (subdomain, reportExplorerPath) => (report: IReport, navigate?: NavigateFunction) => {
    if (typeof navigate === 'function') {
      navigate(
        makeReportExplorerPathReactRouterVersion(subdomain, report.id, report.reportConfigurationId, report.savedViewId)
      );
    } else {
      hardNavigate(reportExplorerPath(subdomain, report.id, report.reportConfigurationId, report.savedViewId));
    }
  }
);

export const selectNavigateToEditReport = createSelector(
  getEntitySubdomain,
  selectCurrentReport,
  selectCurrentReportConfiguration,
  selectCurrentSavedView,
  (entitySubdomain, currentReport, currentReportConfiguration, currentSavedView) => (navigate?: NavigateFunction) => {
    const rcId =
      currentReportConfiguration.id ??
      currentSavedView?.reportConfiguration ??
      currentReport.defaultReportConfiguration;

    hybridNavigate({
      reactRouter: {
        navigate,
        path: multiDatasetReportEditPathReactRouterVersion(
          entitySubdomain,
          currentReport.id,
          rcId,
          currentSavedView?.id || ''
        )
      },
      composeAppRouter: {
        path: multiDatasetReportEditPath(entitySubdomain, currentReport.id, rcId, currentSavedView?.id || '')
      }
    });
  }
);

export const selectCancelEditModeCallback = createSelector(
  getEntitySubdomain,
  selectCurrentReport,
  selectCurrentSavedView,
  (entitySubdomain, currentReport, currentSavedView) => (navigate?: NavigateFunction) => {
    const reportConfigurationId = currentSavedView?.reportConfiguration || currentReport.defaultReportConfiguration;

    hybridNavigate({
      reactRouter: {
        navigate,
        path: multiDatasetReportViewPathReactRouterVersion(
          entitySubdomain,
          currentReport.id,
          reportConfigurationId,
          currentSavedView?.id || ''
        )
      },
      composeAppRouter: {
        path: multiDatasetReportViewPath(
          entitySubdomain,
          currentReport.id,
          reportConfigurationId,
          currentSavedView?.id || ''
        )
      }
    });
  }
);

export const selectNavigateToReportExplorerSavedView = createSelector(
  getEntitySubdomain,
  (subdomain) => (reportConfigurationId: string, savedView: ISavedView) => (navigate: NavigateFunction) => {
    navigate(reportSavedViewSuffix(subdomain, reportConfigurationId, savedView));
  }
);

export const selectNavigateToReportSavedViewEdit = createSelector(
  getEntitySubdomain,
  (subdomain) => (reportId: string, viewId: string) => (navigate?: NavigateFunction) => {
    if (typeof navigate === 'function') {
      navigate(reportSavedViewEditPathReactRouterVersion(subdomain, reportId, viewId));
    } else {
      hardNavigate(reportSavedViewEditPath(subdomain, reportId, viewId));
    }
  }
);

export const selectNavigateToReportViews = createSelector(
  getEntitySubdomain,
  (subdomain) => (reportId, navigate?: NavigateFunction) => {
    if (typeof navigate === 'function') {
      navigate(reportSavedViewsPathReactRouterVersion(subdomain, { reportId }));
    } else {
      hardNavigate(reportSavedViewsPath(subdomain, { reportId }));
    }
  }
);

export const selectNavigateToReportSettings = createSelector(
  getEntitySubdomain,
  (subdomain) => (reportId) => (navigate?: NavigateFunction) => {
    hybridNavigate({
      reactRouter: { navigate, path: reportSettingsPathReactRouterVersion(subdomain, reportId) },
      composeAppRouter: { path: reportSettingsPath(subdomain, reportId) }
    });
  }
);

export const selectCanExportPdf = createSelector(
  selectPdfExportFlag,
  selectCurrentReport,
  selectRouteName,
  (pdfExportFlagEnabled, currentReport, routeName) =>
    (pdfExportFlagEnabled && !!currentReport) || routeName === 'StandardReportExplorer'
);

export const selectDisableExportPdfButton = createSelector(
  selectDataTableStatus,
  selectVisualizationStatus,
  selectDimensionalTableQueryStatuses,
  selectIsReportDimensional,
  (dataTableStatus, visualizationStatus, dimensionalStatus, isReportDimensional): boolean => {
    if (isReportDimensional) {
      // wait until we have the group data before you can export since it's already being loaded
      return dimensionalStatus.length === 0 || !dimensionalStatus.every((status) => status === DataTableStatus.Current);
    }

    return dataTableStatus === DataTableStatus.Loading || visualizationStatus === VisualizationStatus.Loading;
  }
);

export const selectFiltersForExport = createSelector(
  reportLocaleText,
  selectIsReportDimensional,
  selectDetailTableColumns,
  selectDetailTableFilterBooleanOperators,
  selectCurrentFilters,
  selectDimensionalGlobalSegmentFilters,
  selectCurrentReportConfiguration,
  (
    reportText,
    isDimensional,
    columns,
    booleanOperators,
    nonDimensionalFilters,
    dimensionalGlobalFilters,
    reportConfig
  ) => {
    const columnsColumn = reportConfig?.dimensionalSummaryQueries[0]?.dimension?.columnsColumn;

    return formatFiltersForPdfExport({
      reportText,
      isDimensional,
      columns,
      dimensionalGlobalFilters,
      nonDimensionalFilters,
      booleanOperators,
      columnsColumnDisplayName: columnsColumn?.displayName
    });
  }
);

export const selectReportHeaderProps = createStructuredSelector<any, IPageHeaderProps>({
  expandedGroupByNodes: selectGroupByExpandedNodes,
  reportsHref: selectReportsHref,
  canExportPdf: selectCanExportPdf,
  disableExportPdfButton: selectDisableExportPdfButton,
  currentReport: selectCurrentReport,
  currentSavedView: selectCurrentSavedView,
  currentSavedViewEditSavedView: selectCurrentSavedViewEditSavedView,
  localeText: reportLocaleText,
  title: selectCurrentReportTitle,
  description: selectCurrentReportDescription,
  dataUpdatedAt: selectCurrentSourceUpdatedAt,
  canCreateReport: selectCanCreateReport,
  canExportDetailTable: createSelector(
    selectDataTableTotalRowCount,
    selectIsReportDimensional,
    (count, isReportDimensional) => {
      return !isReportDimensional && count <= 10000;
    }
  ),
  canExportVisualization: createSelector(selectIsReportDimensional, (isReportDimensional) => !isReportDimensional),
  readyToExportPdf: createSelector(
    selectFetchingPdfExportData,
    selectPdfExportTooMuchData,
    (fetching, tooMuchData) => !fetching && !tooMuchData
  ),
  currentFilters: selectFiltersForExport,
  clearExportPdfData: () => () => {
    reportExplorerActionCreators.setPdfExportData({
      detailTableData: [],
      detailTableTotalsData: [],
      accountSummaryTableData: []
    });
  },
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  createPermalink: () => () => reportEffects.createReportPermalink(),
  createCopy: () => () => modalActionCreators.setModalComponentType(ReportModalTypes.CD_COPY_REPORT),
  isShareModalActive: createSelector(
    selectModalComponentType,
    (modalComponentType) => modalComponentType === ReportModalTypes.REPORT_SHARE
  ),
  currentTemplate: selectCurrentTemplate,
  exportPdf: () => reportPdfEffects.exportPdf,
  fetchExportPdfData: () => reportPdfEffects.fetchDetailTableExportPdfData,
  fetchSummaryTableData: () => reportPdfEffects.fetchAccountSummaryTableData,
  pdfExportTooMuchData: selectPdfExportTooMuchData,
  visualizationProps: selectReportExportVisualizationProps,
  exportSummaryTableProps: selectPrintableSummaryTableProps,
  generatePdfPreview: () => reportPdfEffects.generatePdfPreview,
  exportDetailTableProps: selectPrintableDetailTableProps,
  onClickEditReport: selectNavigateToEditReport,
  reportDataUpdateStatus: selectReportDataUpdateStatus,
  subdomain: getEntitySubdomain,
  entityUuid: getEntityUuid,
  toastProps: selectMuiToast,
  isCoAMigrationEnabled: selectIsCoaMigrationEnabledFlag
});

export const selectReportExplorerTabLabel = createSelector(reportLocaleText, (msgs) => msgs.report.explorerTab);
export const selectViewsTabLabel = createSelector(reportLocaleText, (msgs) => msgs.report.viewsTab);

export const selectReportNavigationProps = createStructuredSelector<any, IReportNavigationProps>({
  reportExplorerTabLabel: selectReportExplorerTabLabel,
  viewsTabLabel: selectViewsTabLabel,
  isDimensional: selectIsReportDimensional,
  isStandard: selectIsInStandardReport,
  activeTabIndex: createSelector(selectRouteName, selectIsReportDimensional, (name, isDimensional) => {
    if (isDimensional) {
      switch (name) {
        case 'ReportSettings':
          return 2;
        case 'ReportExplorer':
        case 'ReportSavedViews': // shouldn't happen, but a safe catch
          return 1;
        default:
          break;
      }
    } else {
      switch (name) {
        case 'ReportSettings':
          return 2;
        case 'ReportExplorer':
          return 1;
        case 'ReportSavedViews':
          return 0;
        default:
          break;
      }
    }
  }),
  onTabIndexChanged: createSelector(
    selectCurrentReport,
    getEntitySubdomain,
    selectCurrentReportConfiguration,
    selectCurrentSavedView,
    (currentReport, subdomain, currentReportConfiguration, currentSavedView) => {
      return (selectedTabIndex: number) => {
        if (currentReport) {
          switch (selectedTabIndex) {
            case 0:
              return reportSavedViewsPathReactRouterVersion(subdomain, { reportId: currentReport.id });
            case 1:
              return makeReportExplorerPathReactRouterVersion(
                subdomain,
                currentReport.id,
                currentReportConfiguration.id,
                currentSavedView?.id
              );
            case 2:
              return reportSettingsPathReactRouterVersion(subdomain, currentReport.id);
            default:
              break;
          }
        }
      };
    }
  )
});
